import { useState, useEffect } from "react";
import { AdminLayout } from "@/project/jain_elibrary-admin/components/layout/AdminLayout";
import { Button } from "@/project/jain_elibrary-admin/components/ui/button";
import { Input } from "@/project/jain_elibrary-admin/components/ui/input";
import { Label } from "@/project/jain_elibrary-admin/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/project/jain_elibrary-admin/components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/project/jain_elibrary-admin/components/ui/card";
import { Checkbox } from "@/project/jain_elibrary-admin/components/ui/checkbox";
import {
  Download,
  Loader2,
  FileText,
  FileSpreadsheet,
  FileJson,
  Filter,
  Search,
  Eye,
  BookOpen,
} from "lucide-react";
import { adminService, Statistics } from "../../services/adminService";
import { getPreviewCountMatchingStatistics } from "./exportPreviewStats";
import { toast } from "sonner";
import { showErrorToast } from "@/utils/errorHandler";

type ExportFormat = "csv" | "excel" | "json";

interface BookFilters {
  category?: string;
  language?: string;
  search?: string;
}

interface ExportProgress {
  currentPage: number;
  totalPages: number;
  fetchedRecords: number;
  status: string;
}

// Available book fields for export
const BOOK_FIELDS = [
  { key: "sr_no", label: "SR No" },
  { key: "title_english", label: "Title (English)" },
  { key: "title_language", label: "Title (Native)" },
  { key: "author", label: "Author" },
  { key: "editor", label: "Editor" },
  { key: "translator", label: "Translator" },
  { key: "publisher", label: "Publisher" },
  { key: "pub_year", label: "Publication Year" },
  { key: "pages", label: "Pages" },
  { key: "language", label: "Language" },
  { key: "classification", label: "Classification" },
  { key: "downloads", label: "Downloads" },
  { key: "views", label: "Views" },
];

// Download CSV function
const downloadCSVFile = (csvData: string, filename: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      if (!csvData || csvData.length === 0) {
        reject(new Error("CSV data is empty"));
        return;
      }

      const BOM = "\uFEFF";
      const csvWithBOM = BOM + csvData;
      const blob = new Blob([csvWithBOM], {
        type: "text/csv;charset=utf-8;",
      });

      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = `${filename}.csv`;
      link.style.display = "none";
      document.body.appendChild(link);

      requestAnimationFrame(() => {
        try {
          link.click();
          setTimeout(() => {
            if (document.body.contains(link)) {
              document.body.removeChild(link);
            }
            URL.revokeObjectURL(url);
            resolve();
          }, 200);
        } catch (clickError) {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          URL.revokeObjectURL(url);
          reject(clickError);
        }
      });
    } catch (error) {
      reject(error);
    }
  });
};

// Download JSON function
const downloadJSONFile = (jsonData: any, filename: string): void => {
  const jsonString = JSON.stringify(jsonData, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Download Excel (CSV with .xlsx extension - simplified)
const downloadExcelFile = (
  csvData: string,
  filename: string
): Promise<void> => {
  return downloadCSVFile(csvData, filename.replace(".xlsx", ""));
};

const ExportBook = () => {
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState<BookFilters>({
    category: "",
    language: "",
    search: undefined,
  });
  const [exportFormat, setExportFormat] = useState<ExportFormat>("csv");
  const [selectedFields, setSelectedFields] = useState<Set<string>>(
    new Set([
      "sr_no",
      "title_english",
      "author",
      "publisher",
      "language",
      "downloads",
    ])
  );
  const [exportProgress, setExportProgress] = useState<ExportProgress>({
    currentPage: 0,
    totalPages: 0,
    fetchedRecords: 0,
    status: "",
  });
  const [previewResults, setPreviewResults] = useState<any[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [showPreview, setShowPreview] = useState(false);

  // Hardcoded list of 11 languages available in the system
  const languages = [
    { name: "Apabhramsha" },
    { name: "Braj" },
    { name: "Devnagari" },
    { name: "Dhundari" },
    { name: "English" },
    { name: "Germany" },
    { name: "Gujarati" },
    { name: "Hindi" },
    { name: "Marathi" },
    { name: "Prakrita" },
    { name: "Sanskrit" },
  ];

  useEffect(() => {
    fetchFilterOptions();
    adminService
      .getStatistics()
      .then(setStatistics)
      .catch(() => {});
  }, []);

  const fetchFilterOptions = async () => {
    // This function is kept for potential future use
    // Currently, languages are hardcoded and no other filter options are needed
    // Future: Could fetch authors, publishers, categories here if needed
  };

  // Fetch all books with pagination
  const fetchAllBooks = async (params: any = {}): Promise<any[]> => {
    const allBooks: any[] = [];
    let page = 1;
    const pageSize = 100;
    let hasMore = true;
    let totalCount = 0;
    let estimatedPages = 1;

    while (hasMore) {
      try {
        setExportProgress({
          currentPage: page,
          totalPages: estimatedPages,
          fetchedRecords: allBooks.length,
          status: `Fetching page ${page}...`,
        });

        const response = await adminService.getBooks({
          page,
          page_size: pageSize,
          ...params,
        });

        if (page === 1 && response.count) {
          totalCount = response.count;
          estimatedPages = Math.ceil(totalCount / pageSize);
        }

        allBooks.push(...response.results);

        if (!response.next) {
          hasMore = false;
        } else {
          page++;
        }
      } catch (error: any) {
        console.error(`Error fetching books page ${page}:`, error);
        showErrorToast(error, `Failed to fetch books page ${page}`);
        hasMore = false;
      }
    }

    return allBooks;
  };

  // Convert books to CSV - includes ALL columns
  const convertToCSV = (books: any[]): string => {
    if (books.length === 0) {
      return "";
    }

    // Get all unique keys from records - include ALL fields
    const allKeys = new Set<string>();
    books.forEach(book => {
      Object.keys(book).forEach(key => {
        // Exclude internal metadata fields that start with underscore
        if (!key.startsWith("_")) {
          allKeys.add(key);
        }
      });
    });

    // Sort keys for consistent column order
    const headers = Array.from(allKeys).sort();

    if (headers.length === 0) {
      return "";
    }

    // Create CSV rows
    const rows = books.map(book => {
      return headers.map(header => {
        const value = book[header];
        const stringValue =
          value !== null && value !== undefined ? String(value) : "";
        if (
          stringValue.includes(",") ||
          stringValue.includes('"') ||
          stringValue.includes("\n")
        ) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      });
    });

    return [headers.join(","), ...rows.map(row => row.join(","))].join("\n");
  };

  // Client-side search filter function (backend search not working for books)
  const filterBooksBySearch = (books: any[], searchTerm: string): any[] => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return books;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return books.filter(book => {
      // Search in multiple fields
      const titleEnglish = (book.title_english ?? "").toLowerCase();
      const titleLanguage = (book.title_language ?? "").toLowerCase();
      const author = (book.author ?? "").toLowerCase();
      const publisher = (book.publisher ?? "").toLowerCase();
      const srNo = (book.sr_no ?? "").toString().toLowerCase();
      const editor = (book.editor ?? "").toLowerCase();
      const translator = (book.translator ?? "").toLowerCase();

      /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
      return (
        titleEnglish.includes(searchLower) ||
        titleLanguage.includes(searchLower) ||
        author.includes(searchLower) ||
        publisher.includes(searchLower) ||
        srNo.includes(searchLower) ||
        editor.includes(searchLower) ||
        translator.includes(searchLower)
      );
      /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
    });
  };

  // Preview filtered results
  const handlePreview = async () => {
    try {
      setPreviewLoading(true);
      setShowPreview(false);
      toast.info("Loading filtered results...");

      const params: any = {
        page: 1,
        page_size: 100, // Show first 100 results in preview
      };

      if (filters.category && filters.category !== "all") {
        params.category = filters.category;
      }
      if (
        filters.language &&
        filters.language !== "all" &&
        filters.language.trim()
      ) {
        params.language = filters.language.trim();
      }
      if (filters.search?.trim()) {
        params.search = filters.search.trim();
      }

      // Fetch books from backend (backend search not working, so we'll filter client-side)
      const response = await adminService.getBooks(params);
      let filteredResults = response.results ?? [];
      const hasBookListFilters = [
        Boolean(filters.search?.trim()),
        Boolean(filters.category && filters.category !== "all"),
        Boolean(
          filters.language &&
            filters.language !== "all" &&
            filters.language.trim()
        ),
      ].some(Boolean);

      let filteredCount = getPreviewCountMatchingStatistics(
        "books",
        statistics,
        response.count ?? 0,
        hasBookListFilters
      );

      // Apply client-side filtering if search is provided (backend search not working for books)
      if (filters.search?.trim()) {
        // Fetch all pages for client-side filtering
        const allBooks: any[] = [];
        let page = 1;
        const pageSize = 500;
        let hasMore = true;
        // Upper bound avoids infinite loops if the API omits `next` incorrectly
        const maxPages = 500;

        while (hasMore && page <= maxPages) {
          try {
            const pageParams = { ...params, page, page_size: pageSize };
            const pageResponse = await adminService.getBooks(pageParams);
            allBooks.push(...(pageResponse.results ?? []));

            if (!pageResponse.next || pageResponse.results.length < pageSize) {
              hasMore = false;
            } else {
              page++;
            }
          } catch (error) {
            console.error(`Error fetching page ${page}:`, error);
            hasMore = false;
          }
        }

        // Apply client-side search filter
        filteredResults = filterBooksBySearch(allBooks, filters.search.trim());
        filteredCount = filteredResults.length;
      }

      setPreviewResults(filteredResults.slice(0, 100)); // Show first 100 in preview
      setPreviewTotal(filteredCount);
      setShowPreview(true);

      if (filteredCount === 0) {
        toast.warning("No books found matching your filters");
      } else {
        toast.success(
          `Found ${filteredCount.toLocaleString()} book(s). Showing first ${Math.min(filteredResults.length, 100)} in preview.`
        );
      }
    } catch (error: any) {
      console.error("Error previewing results:", error);
      showErrorToast(error, "Failed to load preview. Please try again.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setExportProgress({
        currentPage: 0,
        totalPages: 0,
        fetchedRecords: 0,
        status: "Starting export...",
      });

      toast.info("Fetching books data... This may take a few minutes.");

      // Build filter params
      const params: any = {};
      if (filters.category && filters.category !== "all") {
        params.category = filters.category;
      }
      if (
        filters.language &&
        filters.language !== "all" &&
        filters.language.trim()
      ) {
        params.language = filters.language.trim();
      }

      if (filters.search?.trim()) {
        params.search = filters.search.trim();
      }

      let allBooks: any[] = [];

      // If search is provided, use same client-side filtering logic as preview
      if (filters.search?.trim()) {
        setExportProgress({
          currentPage: 0,
          totalPages: 0,
          fetchedRecords: 0,
          status: "Fetching books for search filtering...",
        });

        // Fetch all pages for client-side filtering (backend search not working for books)
        let page = 1;
        const pageSize = 500;
        let hasMore = true;
        const maxPages = 100; // Allow fetching all pages if needed

        while (hasMore && page <= maxPages) {
          try {
            setExportProgress({
              currentPage: page,
              totalPages: maxPages,
              fetchedRecords: allBooks.length,
              status: `Fetching page ${page} for filtering...`,
            });

            const pageParams = { ...params, page, page_size: pageSize };
            const pageResponse = await adminService.getBooks(pageParams);
            allBooks.push(...(pageResponse.results ?? []));

            if (!pageResponse.next || pageResponse.results.length < pageSize) {
              hasMore = false;
            } else {
              page++;
            }
          } catch (error) {
            console.error(`Error fetching page ${page}:`, error);
            hasMore = false;
          }
        }

        // Apply client-side search filter
        setExportProgress({
          currentPage: 0,
          totalPages: 0,
          fetchedRecords: allBooks.length,
          status: `Filtering ${allBooks.length} books by search term...`,
        });
        allBooks = filterBooksBySearch(allBooks, filters.search.trim());
      } else {
        // No search - fetch all books normally
        allBooks = await fetchAllBooks(params);
      }

      if (allBooks.length === 0) {
        toast.warning("No books found to export");
        setExporting(false);
        return;
      }

      setExportProgress({
        currentPage: 0,
        totalPages: 0,
        fetchedRecords: allBooks.length,
        status: "Converting to export format...",
      });

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `books_export_${timestamp}`;

      // Export based on format
      if (exportFormat === "json") {
        downloadJSONFile(allBooks, filename);
        toast.success(`Successfully exported ${allBooks.length} books to JSON`);
      } else if (exportFormat === "csv" || exportFormat === "excel") {
        const csvData = convertToCSV(allBooks);
        if (exportFormat === "excel") {
          await downloadExcelFile(csvData, filename);
        } else {
          await downloadCSVFile(csvData, filename);
        }
        toast.success(
          `Successfully exported ${allBooks.length} books to ${exportFormat.toUpperCase()}`
        );
      }

      setExportProgress({
        currentPage: 0,
        totalPages: 0,
        fetchedRecords: allBooks.length,
        status: "Export completed!",
      });
    } catch (error: any) {
      console.error("Error exporting books:", error);
      showErrorToast(error, "Failed to export books");
    } finally {
      setExporting(false);
    }
  };

  const toggleField = (fieldKey: string) => {
    const newFields = new Set(selectedFields);
    if (newFields.has(fieldKey)) {
      newFields.delete(fieldKey);
    } else {
      newFields.add(fieldKey);
    }
    setSelectedFields(newFields);
  };

  const selectAllFields = () => {
    setSelectedFields(new Set(BOOK_FIELDS.map(f => f.key)));
  };

  const deselectAllFields = () => {
    setSelectedFields(new Set());
  };

  const totalBooks = statistics?.total_books ?? 0;

  return (
    <AdminLayout>
      <div className="min-w-0 space-y-4 md:space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-foreground md:text-3xl">
              Export Books Data
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Export books with custom filters and field selection
            </p>
          </div>
        </div>

        {/* Statistics Card */}
        {statistics && (
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="flex items-center gap-3">
                  <BookOpen className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Total Books</p>
                    <p className="text-2xl font-bold">
                      {totalBooks.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Export Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="language">Language</Label>
                <Select
                  value={filters.language ?? "all"}
                  onValueChange={value =>
                    setFilters({
                      ...filters,
                      language: value === "all" ? undefined : value,
                    })
                  }
                >
                  <SelectTrigger id="language">
                    <SelectValue placeholder="All Languages" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Languages</SelectItem>
                    {languages.map(language => (
                      <SelectItem key={language.name} value={language.name}>
                        {language.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Input
                    id="search"
                    placeholder="Search in titles, authors, publishers..."
                    value={filters.search ?? ""}
                    onChange={e =>
                      setFilters({
                        ...filters,
                        search: e.target.value || undefined,
                      })
                    }
                    onKeyDown={e => {
                      if (e.key === "Enter" && !previewLoading) {
                        e.preventDefault();
                        handlePreview();
                      }
                    }}
                    className="pr-10"
                  />
                  {filters.search && (
                    <button
                      type="button"
                      onClick={() => {
                        setFilters({ ...filters, search: undefined });
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label="Clear search"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between pt-4">
              {filters.search && filters.search.trim().length > 0 && (
                <div className="text-sm text-muted-foreground">
                  Active search:{" "}
                  <span className="font-medium text-foreground">
                    &quot;{filters.search}&quot;
                  </span>
                </div>
              )}
              <Button
                onClick={handlePreview}
                disabled={previewLoading}
                variant="outline"
                className="flex items-center gap-2"
              >
                {previewLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4" />
                    Preview Results
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview Results Table */}
        {showPreview && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Filtered Results Preview
                </span>
                <span className="text-sm font-normal text-muted-foreground">
                  {previewTotal.toLocaleString()} total book(s) found
                  {previewResults.length < previewTotal &&
                    ` (Showing first ${previewResults.length})`}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {previewResults.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  No books found matching your filters
                </div>
              ) : (
                <div
                  className="w-full overflow-x-auto overflow-y-auto rounded-lg border"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "60vh",
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  <table
                    className="table-fixed border-collapse"
                    style={{
                      minWidth: "800px",
                      width: "100%",
                      tableLayout: "fixed",
                    }}
                  >
                    <thead className="bg-muted">
                      <tr>
                        <th className="h-12 w-24 whitespace-nowrap border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          SR No
                        </th>
                        <th className="h-12 min-w-[200px] border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Title (English)
                        </th>
                        <th className="h-12 min-w-[200px] border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Title (Native)
                        </th>
                        <th className="h-12 min-w-[150px] border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Author
                        </th>
                        <th className="h-12 min-w-[120px] border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Publisher
                        </th>
                        <th className="h-12 w-24 whitespace-nowrap border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Language
                        </th>
                        <th className="h-12 w-20 whitespace-nowrap border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Year
                        </th>
                        <th className="h-12 w-20 whitespace-nowrap border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Downloads
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewResults.map((book, index) => (
                        <tr
                          key={book.sr_no ?? index}
                          className="border-b transition-colors hover:bg-muted/50"
                        >
                          <td className="whitespace-nowrap p-4 align-middle font-mono text-xs">
                            {book.sr_no ?? "-"}
                          </td>
                          <td className="min-w-[200px] p-4 align-middle">
                            <div
                              className="max-w-[300px] truncate"
                              title={book.title_english ?? "-"}
                            >
                              {book.title_english ?? "-"}
                            </div>
                          </td>
                          <td className="min-w-[200px] p-4 align-middle">
                            <div
                              className="max-w-[300px] truncate"
                              title={book.title_language ?? "-"}
                            >
                              {book.title_language ?? "-"}
                            </div>
                          </td>
                          <td className="min-w-[150px] p-4 align-middle">
                            <div
                              className="max-w-[200px] truncate"
                              title={book.author ?? "-"}
                            >
                              {book.author ?? "-"}
                            </div>
                          </td>
                          <td className="min-w-[120px] p-4 align-middle">
                            <div
                              className="max-w-[180px] truncate"
                              title={book.publisher ?? "-"}
                            >
                              {book.publisher ?? "-"}
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-4 align-middle">
                            {book.language ?? "-"}
                          </td>
                          <td className="whitespace-nowrap p-4 align-middle">
                            {book.pub_year ?? "-"}
                          </td>
                          <td className="whitespace-nowrap p-4 align-middle">
                            {book.downloads ?? 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Custom Field Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Select Fields to Export</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Choose which fields to include in the exported file
            </p>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAllFields}>
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={deselectAllFields}>
                Deselect All
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
              {BOOK_FIELDS.map(field => (
                <div key={field.key} className="flex items-center space-x-2">
                  <Checkbox
                    id={field.key}
                    checked={selectedFields.has(field.key)}
                    onCheckedChange={() => toggleField(field.key)}
                  />
                  <Label
                    htmlFor={field.key}
                    className="cursor-pointer text-sm font-normal"
                  >
                    {field.label}
                  </Label>
                </div>
              ))}
            </div>
            {selectedFields.size === 0 && (
              <p className="mt-4 text-sm text-muted-foreground">
                ⚠️ No fields selected. Default fields will be used.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Export Format Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Export Format</CardTitle>
          </CardHeader>
          <CardContent className="min-w-0 overflow-x-hidden">
            <div className="grid w-full min-w-0 grid-cols-1 gap-3 xl:grid-cols-[repeat(3,minmax(0,1fr))]">
              <Button
                variant={exportFormat === "csv" ? "default" : "outline"}
                onClick={() => setExportFormat("csv")}
                className="flex min-h-10 w-full min-w-0 max-w-full items-center justify-center gap-2 whitespace-normal"
              >
                <FileText className="h-4 w-4 shrink-0" />
                CSV
              </Button>
              <Button
                variant={exportFormat === "excel" ? "default" : "outline"}
                onClick={() => setExportFormat("excel")}
                className="flex min-h-10 w-full min-w-0 max-w-full items-center justify-center gap-2 whitespace-normal"
              >
                <FileSpreadsheet className="h-4 w-4 shrink-0" />
                Excel
              </Button>
              <Button
                variant={exportFormat === "json" ? "default" : "outline"}
                onClick={() => setExportFormat("json")}
                className="flex min-h-10 w-full min-w-0 max-w-full items-center justify-center gap-2 whitespace-normal"
              >
                <FileJson className="h-4 w-4 shrink-0" />
                JSON
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Export Progress */}
        {exporting && (
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="font-medium">{exportProgress.status}</span>
                </div>
                {exportProgress.totalPages > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Progress:</span>
                    <span className="font-medium">
                      Page {exportProgress.currentPage} of{" "}
                      {exportProgress.totalPages}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Records Fetched:
                  </span>
                  <span className="font-medium">
                    {exportProgress.fetchedRecords.toLocaleString()}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Export Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleExport}
            disabled={exporting || loading}
            size="lg"
            className="flex items-center gap-2"
          >
            {exporting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Export Books
              </>
            )}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ExportBook;
